import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Divider,
  Stack,
  Link,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Avatar,
  Badge,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { quoteService } from '../../Services/quote.service';
import { API } from '../../Services/axios';
import { URLS } from '../../Services/Urls';
import { toast } from 'react-toastify';

// Couleurs des statuts
const getStatusColor = (status) => {
  switch (status) {
    case 'En attente':
      return 'warning';
    case 'Traité':
      return 'success';
    case 'Rejeté':
      return 'error';
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'warning';
    case 'pending':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':
    case 'Traité':
      return <CheckCircleIcon color="success" />;
    case 'in_progress':
    case 'En attente':
      return <PendingIcon color="warning" />;
    case 'pending':
      return <UncheckedIcon color="disabled" />;
    default:
      return <UncheckedIcon color="disabled" />;
  }
};

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`quote-tabpanel-${index}`}
      aria-labelledby={`quote-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const QuoteDetailsTabs = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState({});
  const [loadingSections, setLoadingSections] = useState(false);

  useEffect(() => {
    fetchQuoteDetails();
  }, [id]);

  const fetchQuoteDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const quoteData = await quoteService.getQuoteDetails(id);
      console.log('Détails du devis reçus:', quoteData);
      console.log('Items du devis:', quoteData.items);
      console.log('Nombre d\'items:', quoteData.items ? quoteData.items.length : 'undefined');
      
      const transformedQuote = quoteService.transformQuoteForDisplay(quoteData);
      console.log('Quote transformé:', transformedQuote);
      setQuote(transformedQuote);
      
      // Récupérer les sections et produits si des items existent
      if (quoteData.items && quoteData.items.length > 0) {
        await fetchSectionsAndProducts(quoteData.items);
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement des détails du devis:', error);
      setError('Erreur lors du chargement des détails du devis');
      toast.error('Erreur lors du chargement des détails du devis');
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionsAndProducts = async (items) => {
    try {
      setLoadingSections(true);
      
      console.log('Items du devis:', items);
      
      const productsData = {};
      const sectionsData = {};
      
      // Récupérer les détails de chaque section (qui contient déjà les produits)
      const uniqueSectionIds = [...new Set(items.map(item => item.section).filter(Boolean))];
      
      for (const sectionId of uniqueSectionIds) {
        try {
          const sectionResponse = await API.get(`project/section/${sectionId}/`);
          console.log(`Section ${sectionId}:`, sectionResponse.data);
          sectionsData[sectionId] = sectionResponse.data;
          
          // Extraire les produits de la section
          if (sectionResponse.data.products && sectionResponse.data.products.length > 0) {
            sectionResponse.data.products.forEach(contentProduct => {
              if (contentProduct.product) {
                // Utiliser l'ID du content_product comme clé pour correspondre aux items
                productsData[contentProduct.id] = contentProduct.product;
                console.log(`Produit extrait de la section:`, {
                  contentProductId: contentProduct.id,
                  product: contentProduct.product
                });
              }
            });
          }
        } catch (sectionError) {
          console.warn(`Erreur lors du chargement de la section ${sectionId}:`, sectionError);
          // Créer une section par défaut si l'API échoue
          sectionsData[sectionId] = {
            id: sectionId,
            name: `Section ${sectionId}`,
            description: 'Section non trouvée'
          };
        }
      }
      
      // Organiser les items par section
      const sectionsMap = new Map();
      
      items.forEach(item => {
        const sectionId = item.section || 'sans-section';
        if (!sectionsMap.has(sectionId)) {
          const sectionInfo = sectionsData[sectionId] || {
            id: sectionId,
            name: `Section ${sectionId}`,
            description: 'Section non trouvée'
          };
          sectionsMap.set(sectionId, {
            id: sectionId,
            name: sectionInfo.name,
            description: sectionInfo.description,
            items: []
          });
        }
        sectionsMap.get(sectionId).items.push(item);
      });
      
      const finalSectionsData = Array.from(sectionsMap.values());
      
      console.log('Sections finales:', finalSectionsData);
      console.log('Produits extraits:', productsData);
      
      setSections(finalSectionsData);
      setProducts(productsData);
      
    } catch (error) {
      console.error('Erreur lors du chargement des sections et produits:', error);
    } finally {
      setLoadingSections(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderClientInfo = () => {
    if (!quote) return null;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
                <PersonIcon sx={{ mr: 1 }} />
                Informations client
              </Typography>
              <Stack spacing={2}>
                <Typography><strong>Nom :</strong> {quote.name}</Typography>
                <Typography><strong>Email :</strong> {quote.email}</Typography>
                <Typography><strong>Téléphone :</strong> {quote.phone}</Typography>
                <Typography><strong>Adresse :</strong> {quote.siteAddress}</Typography>
                <Typography><strong>Code postal :</strong> {quote.postalCode}</Typography>
                <Typography><strong>Ville :</strong> {quote.city}</Typography> 
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
                <BusinessIcon sx={{ mr: 1 }} />
                Détails du projet
              </Typography>
              <Stack spacing={2}>
                <Typography><strong>Type de projet :</strong> {quote.userInput || 'Non spécifié'}</Typography>
                <Typography><strong>Statut :</strong> 
                  <Chip 
                    label={quote.status} 
                    color={getStatusColor(quote.status)}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography><strong>Besoin d'installateur :</strong> {quote.needInstaller ? 'Oui' : 'Non'}</Typography>
                <Typography><strong>Être rappelé :</strong> {quote.beReminded ? 'Oui' : 'Non'}</Typography>
                <Typography><strong>Nombre d'articles :</strong> {quote.itemsCount}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderProjectDetails = () => {
    if (!quote) return null;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
                <DescriptionIcon sx={{ mr: 1 }} />
                Description du projet
              </Typography>
              <Typography variant="body1" paragraph>
                {quote.comments || 'Aucune description fournie par le client.'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {quote.deliveryInstructions && (
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
                  <LocationIcon sx={{ mr: 1 }} />
                  Instructions de livraison
                </Typography>
                <Typography variant="body1">
                  {quote.deliveryInstructions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderItems = () => {
    console.log('renderItems - quote:', quote);
    console.log('renderItems - quote.items:', quote?.items);
    console.log('renderItems - sections:', sections);
    console.log('renderItems - sections.length:', sections.length);
    
    if (!quote || !quote.items || quote.items.length === 0) {
      return (
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
              <ShoppingCartIcon sx={{ mr: 1 }} />
              Articles du devis
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Aucun article trouvé pour ce devis.
            </Typography>
          </CardContent>
        </Card>
      );
    }


    
    if (loadingSections) {
      return (
        <Card elevation={2}>
          <CardContent>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Stack alignItems="center" spacing={2}>
                <CircularProgress />
                <Typography>Chargement des sections et produits...</Typography>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center" sx={{ mb: 3 }}>
          <ShoppingCartIcon sx={{ mr: 1 }} />
          Articles du devis organisés par sections
        </Typography>
        
        {sections.length === 0 ? (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body1" color="text.secondary">
                Aucune section trouvée pour ce devis.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {sections.map((section, sectionIndex) => (
              <Grid item xs={12} key={section.id || sectionIndex}>
                <Card elevation={3} sx={{ borderRadius: 2 }}>
                  <CardContent>
                     <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
                       <AssignmentIcon sx={{ mr: 1 }} />
                       {section.name || `Section ${sectionIndex + 1}`}
                       {/* <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                         (ID: {section.id})
                       </Typography> */}
                     </Typography>
                    
                    {section.items && section.items.length > 0 ? (
                      <Box>
                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
                          Produits sélectionnés ({section.items.length})
                        </Typography>
                        
                        <Grid container spacing={2}>
                          {section.items.map((item, itemIndex) => {
                            const product = products[item.content_product];
                            return (
                              <Grid item xs={12} sm={6} md={4} key={`${sectionIndex}-${itemIndex}`}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                  <CardContent>
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                      {product ? product.name : `Produit ${item.content_product}`}
                                    </Typography>
                                    
                                    {product && (
                                      <>
                                        {product.price && (
                                          <Typography variant="body2" color="text.secondary">
                                            Prix: {product.price} €
                                          </Typography>
                                        )}
                                        {product.sku && (
                                          <Typography variant="body2" color="text.secondary">
                                            SKU: {product.sku}
                                          </Typography>
                                        )}
                                        {product.description && (
                                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {product.description.length > 100 
                                              ? `${product.description.substring(0, 100)}...` 
                                              : product.description
                                            }
                                          </Typography>
                                        )}
                                      </>
                                    )}
                                    
                                     {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                       ID du produit: {item.content_product}
                                       {product && (
                                         <span> - {product.name}</span>
                                       )}
                                     </Typography> */}
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Aucun produit sélectionné dans cette section.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <div className="content-body">
        <div className="container-fluid">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography>Chargement des détails du devis...</Typography>
            </Stack>
          </Box>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-body">
        <div className="container-fluid">
          <Box p={4}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button onClick={fetchQuoteDetails} variant="outlined">
              Réessayer
            </Button>
            <Button onClick={() => navigate('/quote')} variant="outlined" sx={{ ml: 2 }}>
              Retour aux devis
            </Button>
          </Box>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="content-body">
        <div className="container-fluid">
          <Box p={4}>
            <Typography variant="h5">Devis non trouvé.</Typography>
            <Button onClick={() => navigate('/quote')} sx={{ mt: 2 }} variant="outlined">
              Retour aux devis
            </Button>
          </Box>
        </div>
      </div>
    );
  }

  const tabs = [
    { label: 'Informations client', icon: <PersonIcon /> },
    { label: 'Détails du projet', icon: <BusinessIcon /> },
    { label: 'Articles', icon: <ShoppingCartIcon /> }
  ];

  return (
    <div className="content-body">
      <div className="container-fluid">
        {/* Header du devis */}
        <Box sx={{ bgcolor: 'background.paper', mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 3 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 56, height: 56 }}>
                  <PersonIcon />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h4" fontWeight="bold">
                  Devis #{quote.id}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {quote.name} • {quote.email}
                </Typography>
              </Grid>
              <Grid item sx={{ml: 'auto'}}>
                <Chip 
                  label={quote.status} 
                  color={getStatusColor(quote.status)}
                  sx={{ color: 'white', fontWeight: 'bold' }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Informations rapides */}
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3} justifyContent="space-between" alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Téléphone</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {quote.phone || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Ville</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {quote.city}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Date</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {quote.date}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Articles</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {quote.itemsCount} article(s)
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Onglets */}
        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  textTransform: 'none',
                  fontWeight: 'bold'
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>

          {/* Contenu des onglets */}
          <TabPanel value={activeTab} index={0}>
            {renderClientInfo()}
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            {renderProjectDetails()}
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            {renderItems()}
          </TabPanel>
        </Paper>

        {/* Actions */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => navigate('/quote')}
          >
            Voir tous les devis
          </Button>
        </Box>
      </div>
    </div>
  );
};

export default QuoteDetailsTabs;
